import type { Event, EventHint } from "@sentry/vue"
import axios, { type AxiosError } from "axios"

const SESSION_KEY = "pl-network-session-id"
const NETWORK_FINGERPRINT = "dashboard-client-network-transition"

export interface OfflineInterval {
    startedAtMs: number
    endedAtMs: number
}

export interface ClientNetworkRuntime {
    online: boolean
    currentHref: string
    nowMs: number
    currentOfflineStartedAtMs?: number
    lastOfflineInterval?: OfflineInterval
    sessionId?: string
}

interface NetworkTransitionEvidence {
    reason: "browser-offline" | "request-overlapped-offline"
    requestMethod: string
    requestPath: string
    requestStartedAtMs: number
    requestRejectedAtMs: number
    offlineStartedAtMs: number
    offlineEndedAtMs?: number
    timeoutMs?: number
}

function redactedRequestPath(rawUrl: unknown, currentHref: string): string | undefined {
    if (typeof rawUrl !== "string" || !rawUrl) return undefined

    try {
        const pathname = new URL(rawUrl, currentHref).pathname
        return pathname
            .replace(/^\/(campaigns|videos)\//, "/api/$1/")
            .replace(/^(\/api\/(?:campaigns|videos))\/[^/]+/, "$1/<id>")
    } catch {
        return undefined
    }
}

function timeoutMs(error: AxiosError): number | undefined {
    const configured = error.config?.timeout
    if (typeof configured === "number" && Number.isFinite(configured)) return configured

    const match = error.message.match(/^timeout of (\d+(?:\.\d+)?)ms exceeded$/i)
    return match ? Number(match[1]) : undefined
}

function isTimeoutWithoutResponse(error: AxiosError): boolean {
    if (error.response) return false

    return (
        error.code === "ECONNABORTED" ||
        error.code === "ETIMEDOUT" ||
        /^timeout of \d+(?:\.\d+)?ms exceeded$/i.test(error.message)
    )
}

function overlappingOfflineInterval(
    requestStartedAtMs: number,
    requestRejectedAtMs: number,
    runtime: ClientNetworkRuntime,
): { startedAtMs: number; endedAtMs?: number } | undefined {
    if (
        requestStartedAtMs < 0 ||
        requestRejectedAtMs < requestStartedAtMs ||
        requestRejectedAtMs > runtime.nowMs
    ) {
        return undefined
    }

    if (!runtime.online) {
        const offlineStartedAtMs = runtime.currentOfflineStartedAtMs
        if (
            offlineStartedAtMs === undefined ||
            offlineStartedAtMs < 0 ||
            offlineStartedAtMs > runtime.nowMs
        ) {
            return undefined
        }
        return requestRejectedAtMs > offlineStartedAtMs
            ? { startedAtMs: offlineStartedAtMs }
            : undefined
    }

    const interval = runtime.lastOfflineInterval
    if (!interval) return undefined
    if (interval.startedAtMs < 0 || interval.endedAtMs < interval.startedAtMs) return undefined
    if (interval.endedAtMs > runtime.nowMs) return undefined

    const requestOverlapsInterval =
        requestStartedAtMs < interval.endedAtMs && requestRejectedAtMs > interval.startedAtMs
    return requestOverlapsInterval ? interval : undefined
}

function networkTransitionEvidence(
    hint: EventHint,
    runtime: ClientNetworkRuntime,
): NetworkTransitionEvidence | undefined {
    const originalException = hint.originalException
    if (!axios.isAxiosError(originalException)) return undefined
    if (!isTimeoutWithoutResponse(originalException)) return undefined
    if (originalException.config?.pitchlaneRequestPurpose !== "background-poll") return undefined

    const requestMethod = (originalException.config.method ?? "get").toLowerCase()
    if (requestMethod !== "get" && requestMethod !== "head") return undefined

    const requestStartedAtMs = originalException.config.pitchlaneRequestStartedAtMs
    if (typeof requestStartedAtMs !== "number" || !Number.isFinite(requestStartedAtMs)) {
        return undefined
    }

    const requestRejectedAtMs = originalException.config.pitchlaneRequestRejectedAtMs
    if (typeof requestRejectedAtMs !== "number" || !Number.isFinite(requestRejectedAtMs)) {
        return undefined
    }

    const requestPath = redactedRequestPath(originalException.config.url, runtime.currentHref)
    if (!requestPath) return undefined

    const offlineInterval = overlappingOfflineInterval(
        requestStartedAtMs,
        requestRejectedAtMs,
        runtime,
    )
    if (!offlineInterval) return undefined

    return {
        reason: runtime.online ? "request-overlapped-offline" : "browser-offline",
        requestMethod,
        requestPath,
        requestStartedAtMs,
        requestRejectedAtMs,
        offlineStartedAtMs: offlineInterval.startedAtMs,
        offlineEndedAtMs: offlineInterval.endedAtMs,
        timeoutMs: timeoutMs(originalException),
    }
}

export function classifyClientNetworkEvent(
    event: Event,
    hint: EventHint,
    runtime: ClientNetworkRuntime,
): Event {
    const evidence = networkTransitionEvidence(hint, runtime)
    if (!evidence) return event

    const sessionId = runtime.sessionId
    return {
        ...event,
        level: "info",
        fingerprint: [NETWORK_FINGERPRINT],
        tags: {
            ...event.tags,
            client_network_transition: evidence.reason,
            ...(sessionId ? { network_session: sessionId } : {}),
        },
        contexts: {
            ...event.contexts,
            client_network_transition: {
                reason: evidence.reason,
                browser_online: runtime.online,
                request_method: evidence.requestMethod,
                request_path: evidence.requestPath,
                timeout_ms: evidence.timeoutMs,
                request_started_at_ms: evidence.requestStartedAtMs,
                request_rejected_at_ms: evidence.requestRejectedAtMs,
                offline_started_at_ms: evidence.offlineStartedAtMs,
                offline_ended_at_ms: evidence.offlineEndedAtMs,
            },
        },
        user:
            event.user ??
            (sessionId
                ? {
                      id: `network-session:${sessionId}`,
                  }
                : undefined),
    }
}

export function getOrCreateClientSessionId(): string | undefined {
    try {
        const existing = window.sessionStorage.getItem(SESSION_KEY)
        if (existing) return existing

        const created = window.crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`
        window.sessionStorage.setItem(SESSION_KEY, created)
        return created
    } catch {
        return undefined
    }
}

export function createClientNetworkEventProcessor() {
    let currentOfflineStartedAtMs = window.navigator.onLine
        ? undefined
        : window.performance.now()
    let lastOfflineInterval: OfflineInterval | undefined

    window.addEventListener("offline", () => {
        currentOfflineStartedAtMs ??= window.performance.now()
    })
    window.addEventListener("online", () => {
        if (currentOfflineStartedAtMs === undefined) return
        lastOfflineInterval = {
            startedAtMs: currentOfflineStartedAtMs,
            endedAtMs: window.performance.now(),
        }
        currentOfflineStartedAtMs = undefined
    })

    return (event: Event, hint: EventHint): Event =>
        classifyClientNetworkEvent(event, hint, {
            online: window.navigator.onLine,
            currentHref: window.location.href,
            nowMs: window.performance.now(),
            currentOfflineStartedAtMs,
            lastOfflineInterval,
            sessionId: getOrCreateClientSessionId(),
        })
}
